import React, { forwardRef } from "react";
import { EQUINOX_LOGO_BASE64, REMEDY_LOGO_BASE64 } from "./logoAssets";
import { lightTheme_Primary, lightTheme_Neutral, lightTheme_Success } from "../../../../common/colors/colorTokens";
import Logo from "../../../../common/svgs/logo";

// Font constants matching App.module.css @font-face declarations
const FONT_REGULAR = '"AktivGroteskEx_A_Rg", Arial, Helvetica, sans-serif';
const FONT_MEDIUM = '"AktivGroteskEx_A_Md", Arial, Helvetica, sans-serif';
const FONT_BOLD = '"AktivGroteskEx_A_Bd", Arial, Helvetica, sans-serif';
const FONT_EXTRA_BOLD = '"AktivGroteskEx_A_XBd", "AktivGroteskEx_A_Bd", Arial, Helvetica, sans-serif';

// Semantic color for "younger" indicator — from design system
const COLOR_YOUNGER = lightTheme_Success[60]; // #20BF7D
// Custom muted red for "older" indicator — not in token system
const COLOR_OLDER = "#e07070";

type DesignConfig = {
  background: string;
  brandName: string;
  accentColor: string;
  glowTopColor: string;
  glowBottomColor: string;
  heroGradient: string;
  textColor: string;
  subtextColor: string;
  statsCardBg: string;
  statsCardBorder: string;
  statDividerColor: string;
  footerColor: string;
  logoImage?: string;
  logoWidth?: number;
  accentLineColor?: string;
  hideGlow?: boolean;
  hideFooter?: boolean;
};

// PNOE default: deep blue with subtle blue sphere glows
const PNOE_BACKGROUND = [
  // Top sphere — cool blue
  "radial-gradient(ellipse 45% 35% at 72% 8%, rgba(36, 121, 240, 0.22) 0%, transparent 70%)",
  // Center-left sphere
  "radial-gradient(ellipse 40% 30% at 22% 48%, rgba(16, 100, 219, 0.12) 0%, transparent 65%)",
  // Bottom sphere
  "radial-gradient(ellipse 50% 35% at 55% 92%, rgba(67, 142, 248, 0.15) 0%, transparent 65%)",
  // Subtle rim highlights
  "radial-gradient(ellipse 12% 8% at 70% 6%, rgba(111, 170, 252, 0.12) 0%, transparent 80%)",
  "radial-gradient(ellipse 10% 6% at 52% 94%, rgba(100, 160, 248, 0.08) 0%, transparent 80%)",
  // Dark vignette for readability
  "radial-gradient(ellipse 60% 50% at 50% 50%, transparent 0%, rgba(0, 0, 0, 0.4) 100%)",
  "linear-gradient(170deg, #060a14 0%, #0a0e1a 100%)",
].join(", ");

const DEFAULT_DESIGN: DesignConfig = {
  background: PNOE_BACKGROUND,
  brandName: "PNOE",
  accentColor: lightTheme_Primary[60],
  glowTopColor: "transparent",
  glowBottomColor: "transparent",
  heroGradient:
    `linear-gradient(180deg, ${lightTheme_Primary[20]} 0%, ${lightTheme_Primary[60]} 50%, ${lightTheme_Primary[80]} 100%)`,
  textColor: "#ffffff",
  subtextColor: lightTheme_Primary[40],
  statsCardBg: "rgba(255,255,255,0.04)",
  statsCardBorder: `rgba(67,142,248,0.2)`,
  statDividerColor: `rgba(67,142,248,0.15)`,
  footerColor: lightTheme_Neutral[80],
  hideGlow: true,
  hideFooter: true,
};

// Remedy: warm amber — elegant spheres, dark center for readability
const REMEDY_BACKGROUND = [
  // Top-right sphere — soft warm glow
  "radial-gradient(ellipse 45% 35% at 75% 8%, rgba(200, 150, 60, 0.30) 0%, transparent 70%)",
  // Center-left sphere — subtle warmth
  "radial-gradient(ellipse 40% 30% at 25% 45%, rgba(180, 130, 50, 0.15) 0%, transparent 65%)",
  // Bottom sphere — grounding glow
  "radial-gradient(ellipse 50% 35% at 50% 92%, rgba(190, 140, 55, 0.20) 0%, transparent 65%)",
  // Thin bright rim highlights on spheres
  "radial-gradient(ellipse 12% 8% at 72% 6%, rgba(255, 210, 130, 0.25) 0%, transparent 80%)",
  "radial-gradient(ellipse 10% 6% at 48% 94%, rgba(255, 200, 120, 0.15) 0%, transparent 80%)",
  // Dark vignette — keeps center clean for text
  "radial-gradient(ellipse 60% 50% at 50% 50%, transparent 0%, rgba(0, 0, 0, 0.4) 100%)",
  "linear-gradient(170deg, #0e0a04 0%, #0a0804 100%)",
].join(", ");

// Equinox: teal/cyan — elegant spheres, dark center for readability
const EQUINOX_BACKGROUND = [
  // Top-right sphere
  "radial-gradient(ellipse 45% 35% at 75% 8%, rgba(50, 180, 160, 0.28) 0%, transparent 70%)",
  // Center-left sphere
  "radial-gradient(ellipse 40% 30% at 25% 45%, rgba(40, 155, 140, 0.14) 0%, transparent 65%)",
  // Bottom sphere
  "radial-gradient(ellipse 50% 35% at 50% 92%, rgba(45, 170, 150, 0.18) 0%, transparent 65%)",
  // Thin bright rim highlights
  "radial-gradient(ellipse 12% 8% at 72% 6%, rgba(120, 255, 230, 0.18) 0%, transparent 80%)",
  "radial-gradient(ellipse 10% 6% at 48% 94%, rgba(100, 240, 215, 0.10) 0%, transparent 80%)",
  // Dark vignette
  "radial-gradient(ellipse 60% 50% at 50% 50%, transparent 0%, rgba(0, 0, 0, 0.4) 100%)",
  "linear-gradient(170deg, #040a09 0%, #030807 100%)",
].join(", ");

const EQUINOX_DESIGN: DesignConfig = {
  background: EQUINOX_BACKGROUND,
  brandName: "EQUINOX",
  accentColor: "#5ec4b5",
  glowTopColor: "transparent",
  glowBottomColor: "transparent",
  heroGradient:
    "linear-gradient(180deg, #e0f5f0 0%, #5ec4b5 50%, #2a8a7e 100%)",
  textColor: "#ffffff",
  subtextColor: "#8ec8be",
  statsCardBg: "rgba(255,255,255,0.04)",
  statsCardBorder: "rgba(94,196,181,0.2)",
  statDividerColor: "rgba(94,196,181,0.15)",
  footerColor: "#4b5563",
  logoImage: EQUINOX_LOGO_BASE64,
  logoWidth: 150,
  accentLineColor: "rgba(94,196,181,0.3)",
  hideGlow: true,
  hideFooter: true,
};

const REMEDY_DESIGN: DesignConfig = {
  background: REMEDY_BACKGROUND,
  brandName: "REMEDY",
  accentColor: "#d4a862",
  glowTopColor: "transparent",
  glowBottomColor: "transparent",
  heroGradient:
    "linear-gradient(180deg, #f5ede0 0%, #d4a862 50%, #a07830 100%)",
  textColor: "#f0ebe4",
  subtextColor: "#c4b8a6",
  statsCardBg: "rgba(255,255,255,0.04)",
  statsCardBorder: "rgba(212,168,98,0.2)",
  statDividerColor: "rgba(212,168,98,0.15)",
  footerColor: "#9ca3af",
  logoImage: REMEDY_LOGO_BASE64,
  logoWidth: 170,
  accentLineColor: "rgba(212,168,98,0.3)",
  hideGlow: true,
  hideFooter: true,
};

const PNOE_TESTER_DESIGN: DesignConfig = {
  background:
    "linear-gradient(145deg, #f8f9fa 0%, #e9ecef 30%, #f1f3f5 60%, #ffffff 100%)",
  brandName: "Custom",
  accentColor: "#1B4965",
  glowTopColor: "rgba(27,73,101,0.08)",
  glowBottomColor: "rgba(27,73,101,0.05)",
  heroGradient:
    "linear-gradient(135deg, #1B4965 0%, #2d6a8e 40%, #3d8ab5 100%)",
  textColor: "#1a1a2e",
  subtextColor: "#6b7280",
  statsCardBg: "rgba(0,0,0,0.04)",
  statsCardBorder: "rgba(0,0,0,0.1)",
  statDividerColor: "rgba(0,0,0,0.1)",
  footerColor: "#9ca3af",
};

function getDesignForCustomShare(
  customShare: string | null | undefined
): DesignConfig {
  if (!customShare) return DEFAULT_DESIGN;

  switch (customShare) {
    case "pnoeTester":
      return PNOE_TESTER_DESIGN;
    case "equinox":
      return EQUINOX_DESIGN;
    case "remedy":
      return REMEDY_DESIGN;
    default:
      return DEFAULT_DESIGN;
  }
}

type BioAgeShareCardProps = {
  bioAge: number;
  chronologicalAge: number;
  customShare?: string | null;
};

const BioAgeShareCard = forwardRef<HTMLDivElement, BioAgeShareCardProps>(
  ({ bioAge, chronologicalAge, customShare }, ref) => {
    const design = getDesignForCustomShare(customShare);
    const difference = bioAge - chronologicalAge;
    const isYounger = difference < 0;
    const diffColor = isYounger ? COLOR_YOUNGER : COLOR_OLDER;
    const diffSign = isYounger ? "" : "+";

    const now = new Date();
    const monthYear = now.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });

    return (
      <div ref={ref} style={{ ...styles.container, background: design.background }}>
        {/* Background glow effects */}
        {!design.hideGlow && (
          <>
            <div
              style={{
                ...styles.glowTop,
                background: `radial-gradient(circle, ${design.glowTopColor} 0%, transparent 70%)`,
              }}
            />
            <div
              style={{
                ...styles.glowBottom,
                background: `radial-gradient(circle, ${design.glowBottomColor} 0%, transparent 70%)`,
              }}
            />
          </>
        )}

        {/* Content */}
        <div style={styles.content}>
          {/* Logo */}
          {design.brandName === "PNOE" ? (
            <div style={styles.logoImageContainer}>
              <Logo color="white" width={140} height={34} />
            </div>
          ) : design.logoImage ? (
            <div style={styles.logoImageContainer}>
              <img
                src={design.logoImage}
                alt={design.brandName}
                style={{
                  width: design.logoWidth || 200,
                  height: "auto",
                }}
              />
            </div>
          ) : (
            <div style={styles.logoContainer}>
              <div style={styles.logoIcon}>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <rect width="28" height="28" rx="6" fill={design.accentColor} />
                  <text
                    x="14"
                    y="20"
                    textAnchor="middle"
                    fill="white"
                    fontSize="18"
                    fontWeight="bold"
                    fontFamily="Arial, sans-serif"
                  >
                    {design.brandName.charAt(0)}
                  </text>
                </svg>
              </div>
              <div style={{ ...styles.logoText, color: design.textColor }}>{design.brandName}</div>
            </div>
          )}

          {/* Hero — lifespan ring + big number */}
          <div style={styles.heroSection}>
            {(() => {
              const size = 300;
              const cx = size / 2;
              const cy = size / 2;
              const r = 132;
              const strokeW = 6;
              const circumference = 2 * Math.PI * r;
              const absDiff = Math.abs(difference);
              const maxAge = 84;

              // Arc lengths: 0→bioAge filled with accent, then difference segment
              const bioLength = (bioAge / maxAge) * circumference;
              const chronoLength = (chronologicalAge / maxAge) * circumference;
              const diffLength = Math.abs(chronoLength - bioLength);
              const minAgeLength = Math.min(bioLength, chronoLength);

              const ringId = `ring-${bioAge}-${chronologicalAge}`;

              // Endpoint position for bio age arc
              const bioAngleDeg = (bioAge / maxAge) * 360 - 90;
              const bioAngleRad = bioAngleDeg * (Math.PI / 180);
              const endX = cx + r * Math.cos(bioAngleRad);
              const endY = cy + r * Math.sin(bioAngleRad);

              // Two subtle sparkles — light catches on the arc
              const sparkles = [0.4, 0.7].map((frac, i) => {
                const angle = ((frac * bioAge / maxAge) * 360 - 90) * (Math.PI / 180);
                const offsets = [1, -1];
                return {
                  x: cx + (r + offsets[i]) * Math.cos(angle),
                  y: cy + (r + offsets[i]) * Math.sin(angle),
                  size: [1.8, 2.2][i],
                  opacity: [0.35, 0.45][i],
                };
              });

              return (
                <div style={{
                  position: "relative" as const,
                  width: `${size}px`,
                  height: `${size}px`,
                }}>
                  <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: "visible" as const }}>
                    <defs>
                      <filter id={`glow-${ringId}`}>
                        <feGaussianBlur stdDeviation="3" result="blur" />
                        <feMerge>
                          <feMergeNode in="blur" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                      <filter id={`dotGlow-${ringId}`}>
                        <feGaussianBlur stdDeviation="8" result="coloredBlur" />
                        <feMerge>
                          <feMergeNode in="coloredBlur" />
                          <feMergeNode in="coloredBlur" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                      <filter id={`sparkGlow-${ringId}`}>
                        <feGaussianBlur stdDeviation="1.5" result="blur" />
                        <feMerge>
                          <feMergeNode in="blur" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                      {/* Arc gradient — emerges from nothing, builds toward endpoint */}
                      <linearGradient id={`arcGrad-${ringId}`} gradientUnits="userSpaceOnUse" x1={cx} y1={cy - r} x2={endX} y2={endY}>
                        <stop offset="0%" stopColor={design.accentColor} stopOpacity="0.05" />
                        <stop offset="40%" stopColor={design.accentColor} stopOpacity="0.25" />
                        <stop offset="75%" stopColor={design.accentColor} stopOpacity="0.55" />
                        <stop offset="100%" stopColor={design.accentColor} stopOpacity="0.85" />
                      </linearGradient>
                      {/* Inner shine — offset upward for realistic top-down lighting */}
                      <radialGradient id={`innerShine-${ringId}`} cx="50%" cy="35%" r="55%">
                        <stop offset="0%" stopColor={design.accentColor} stopOpacity="0.08" />
                        <stop offset="40%" stopColor={design.accentColor} stopOpacity="0.03" />
                        <stop offset="100%" stopColor={design.accentColor} stopOpacity="0" />
                      </radialGradient>
                      {/* Large atmospheric glow — wide and gradual */}
                      <radialGradient id={`atmosphere-${ringId}`} cx="50%" cy="50%" r="50%">
                        <stop offset="30%" stopColor={design.accentColor} stopOpacity="0.06" />
                        <stop offset="60%" stopColor={design.accentColor} stopOpacity="0.03" />
                        <stop offset="85%" stopColor={design.accentColor} stopOpacity="0.01" />
                        <stop offset="100%" stopColor={design.accentColor} stopOpacity="0" />
                      </radialGradient>
                      {/* Inner vignette — darkens edges, lightens center */}
                      <radialGradient id={`vignette-${ringId}`} cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="white" stopOpacity="0.02" />
                        <stop offset="70%" stopColor="black" stopOpacity="0" />
                        <stop offset="100%" stopColor="black" stopOpacity="0.06" />
                      </radialGradient>
                      {/* Top-light reflection on upper ring edge */}
                      <radialGradient id={`topLight-${ringId}`} cx="50%" cy="15%" r="50%">
                        <stop offset="0%" stopColor="white" stopOpacity="0.03" />
                        <stop offset="100%" stopColor="white" stopOpacity="0" />
                      </radialGradient>
                    </defs>

                    {/* Atmospheric glow — very wide, bleeds into background */}
                    <circle cx={cx} cy={cy} r={r + 100} fill={`url(#atmosphere-${ringId})`} />

                    {/* Inner shine — glass orb with top-down light */}
                    <circle cx={cx} cy={cy} r={r - strokeW} fill={`url(#innerShine-${ringId})`} />

                    {/* Inner vignette — depth inside the ring */}
                    <circle cx={cx} cy={cy} r={r - strokeW} fill={`url(#vignette-${ringId})`} />

                    {/* Ambient ring fog — extremely wide and faint */}
                    <circle cx={cx} cy={cy} r={r} fill="none" stroke={design.accentColor} strokeOpacity="0.012" strokeWidth={strokeW + 40} />

                    {/* Reflection highlight — thin bright arc on upper-left */}
                    <circle
                      cx={cx} cy={cy} r={r + 1} fill="none"
                      stroke="white" strokeOpacity="0.10" strokeWidth="2"
                      strokeDasharray={`${circumference * 0.15} ${circumference * 0.85}`}
                      strokeDashoffset={circumference * 0.35}
                      strokeLinecap="round"
                      transform={`rotate(-90 ${cx} ${cy})`}
                    />

                    {/* Track — ghost, almost imperceptible */}
                    <circle cx={cx} cy={cy} r={r} fill="none" stroke={design.subtextColor} strokeOpacity="0.01" strokeWidth={strokeW} />

                    {/* Accent fill with gradient: 0 → bio age */}
                    <circle
                      cx={cx} cy={cy} r={r} fill="none"
                      stroke={`url(#arcGrad-${ringId})`} strokeWidth={strokeW}
                      strokeDasharray={`${bioLength} ${circumference - bioLength}`}
                      strokeDashoffset="0"
                      strokeLinecap="round"
                      transform={`rotate(-90 ${cx} ${cy})`}
                    />

                    {/* Difference segment */}
                    {diffLength > 1 && (
                      <circle
                        cx={cx} cy={cy} r={r} fill="none"
                        stroke={isYounger ? COLOR_YOUNGER : COLOR_OLDER}
                        strokeOpacity={0.8}
                        strokeWidth={strokeW + 1}
                        strokeDasharray={`${diffLength} ${circumference - diffLength}`}
                        strokeDashoffset={-minAgeLength}
                        strokeLinecap="round"
                        transform={`rotate(-90 ${cx} ${cy})`}
                        filter={`url(#glow-${ringId})`}
                      />
                    )}

                    {/* Endpoint bloom — soft dissipating light, not a hard dot */}
                    <circle cx={endX} cy={endY} r="28" fill={design.accentColor} fillOpacity="0.06" filter={`url(#dotGlow-${ringId})`} />
                    <circle cx={endX} cy={endY} r="14" fill={design.accentColor} fillOpacity="0.1" />
                    <circle cx={endX} cy={endY} r="6" fill={design.accentColor} fillOpacity="0.3" />
                    <circle cx={endX} cy={endY} r="2" fill="white" fillOpacity="0.7" />

                    {/* Sparkles — glowing dots along the arc */}
                    {sparkles.map((s, i) => (
                      <circle key={i} cx={s.x} cy={s.y} r={s.size} fill="white" fillOpacity={s.opacity} filter={`url(#sparkGlow-${ringId})`} />
                    ))}
                  </svg>

                  {/* Center content */}
                  <div style={{
                    position: "absolute" as const,
                    top: 0, left: 0, right: 0, bottom: 0,
                    display: "flex",
                    flexDirection: "column" as const,
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1,
                  }}>
                    {/* Context label — #3 in hierarchy */}
                    <div style={{
                      fontFamily: FONT_MEDIUM,
                      fontSize: "12px",
                      fontWeight: 500,
                      color: design.subtextColor,
                      letterSpacing: "2.5px",
                      textTransform: "uppercase" as const,
                      marginBottom: "2px",
                    }}>
                      Bio Age
                    </div>
                    {/* Hero number — #1 in hierarchy */}
                    <div style={{
                      fontFamily: FONT_BOLD,
                      fontSize: "96px",
                      fontWeight: 700,
                      lineHeight: "0.9",
                      letterSpacing: "-1px",
                      background: design.heroGradient,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}>
                      {bioAge}
                    </div>
                    {/* Difference — #2 in hierarchy, the emotional hook */}
                    <div style={{
                      fontFamily: FONT_MEDIUM,
                      fontSize: "13px",
                      fontWeight: 500,
                      color: diffColor,
                      letterSpacing: "0.3px",
                      marginTop: "6px",
                    }}>
                      {absDiff} {absDiff === 1 ? "Year" : "Years"} {isYounger ? "Younger" : "Older"}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Stats card */}
          <div style={{ ...styles.statsCard, background: design.statsCardBg, border: `1px solid ${design.statsCardBorder}` }}>
            <div style={styles.statRow}>
              <div style={{ ...styles.statLabel, color: design.subtextColor }}>CHRONOLOGICAL</div>
              <div style={{ ...styles.statValueWhite, color: design.textColor }}>
                {chronologicalAge}
                <span style={styles.statUnit}> yrs</span>
              </div>
            </div>
            <div style={{ ...styles.statDivider, background: design.statDividerColor }} />
            <div style={styles.statRow}>
              <div style={{ ...styles.statLabel, color: design.subtextColor }}>DIFFERENCE</div>
              <div style={{ ...styles.statValueColored, color: diffColor }}>
                {diffSign}
                {difference}
                <span style={{ ...styles.statUnit, color: diffColor }}>
                  {" "}
                  yrs
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          {!design.hideFooter && (
            <div style={{ ...styles.footer, color: design.footerColor }}>{monthYear}</div>
          )}
        </div>
      </div>
    );
  }
);

BioAgeShareCard.displayName = "BioAgeShareCard";

export default BioAgeShareCard;

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: "relative",
    width: "100%",
    maxWidth: "405px",
    aspectRatio: "9 / 16",
    borderRadius: "24px",
    overflow: "hidden",
  },
  glowTop: {
    position: "absolute",
    top: "-20%",
    left: "-20%",
    width: "80%",
    height: "60%",
    borderRadius: "50%",
    pointerEvents: "none",
  },
  glowBottom: {
    position: "absolute",
    bottom: "-10%",
    right: "-20%",
    width: "70%",
    height: "50%",
    borderRadius: "50%",
    pointerEvents: "none",
  },
  content: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "space-between",
    height: "100%",
    padding: "32px 24px",
    boxSizing: "border-box" as const,
  },
  logoImageContainer: {
    position: "absolute" as const,
    top: "32px",
    left: 0,
    right: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  logoIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logoText: {
    fontFamily: FONT_BOLD,
    fontSize: "20px",
    fontWeight: 700,
    color: "#ffffff",
    letterSpacing: "6px",
    textTransform: "uppercase" as const,
  },
  heroSection: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "4px",
    flex: 1,
    justifyContent: "center",
  },
  heroLabel: {
    fontFamily: FONT_REGULAR,
    fontSize: "11px",
    fontWeight: 500,
    color: "#9ca3af",
    letterSpacing: "4px",
    textTransform: "uppercase" as const,
  },
  heroAge: {
    fontFamily: FONT_EXTRA_BOLD,
    fontSize: "110px",
    fontWeight: 800,
    lineHeight: "1",
    letterSpacing: "-2px",
  },
  heroSubtext: {
    fontFamily: FONT_REGULAR,
    fontSize: "16px",
    fontWeight: 400,
    color: "#6b7280",
    letterSpacing: "2px",
    textTransform: "uppercase" as const,
  },
  statsCard: {
    width: "100%",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "20px",
    padding: "20px 24px",
    boxSizing: "border-box" as const,
  },
  statRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  statLabel: {
    fontFamily: FONT_REGULAR,
    fontSize: "10px",
    fontWeight: 500,
    color: "#9ca3af",
    letterSpacing: "1.5px",
    textTransform: "uppercase" as const,
  },
  statValueWhite: {
    fontFamily: FONT_BOLD,
    fontSize: "18px",
    fontWeight: 700,
    color: "#ffffff",
  },
  statValueColored: {
    fontFamily: FONT_BOLD,
    fontSize: "18px",
    fontWeight: 700,
  },
  statUnit: {
    fontFamily: FONT_REGULAR,
    fontSize: "12px",
    fontWeight: 400,
  },
  statDivider: {
    height: "1px",
    background: "rgba(255,255,255,0.1)",
    margin: "12px 0",
  },
  footer: {
    fontFamily: FONT_REGULAR,
    fontSize: "13px",
    fontWeight: 400,
    color: "#4b5563",
    textAlign: "center" as const,
  },
};
